import { useCallback, useRef, useState } from 'react'
import useApiService from 'services/api'
import { useMountEffect } from './useMountEffect'

interface StreamingTranscription {
  isRecording: boolean
  finalTranscript: string
  partialTranscript: string
  error?: string
  start: () => Promise<void>
  stop: () => void
}

interface UseStreamingTranscriptionOptions {
  /**
   * Fires once each time a turn is finalized, with the full accumulated
   * transcript. Use this (not a render-body effect) to react to new speech.
   */
  onTurnFinalized?: (fullTranscript: string) => void
}

// AssemblyAI Universal-Streaming v3 (verified against docs 2026-06-16).
const WS_BASE = 'wss://streaming.assemblyai.com/v3/ws'
const WS_PARAMS =
  'speech_model=universal-streaming-english&sample_rate=16000&encoding=pcm_s16le&format_turns=true'

export function useStreamingTranscription(
  options: UseStreamingTranscriptionOptions = {}
): StreamingTranscription {
  const api = useApiService()
  const [isRecording, setIsRecording] = useState(false)
  const [finalTranscript, setFinalTranscript] = useState('')
  const [partialTranscript, setPartialTranscript] = useState('')
  const [error, setError] = useState<string | undefined>()

  // Latest-callback ref so the onmessage closure (captured once when `start`
  // runs) always calls the current callback without re-opening the WebSocket.
  const onTurnFinalizedRef = useRef(options.onTurnFinalized)
  onTurnFinalizedRef.current = options.onTurnFinalized
  // Accumulates the finalized transcript so we can hand the full text to the
  // callback synchronously (setFinalTranscript is async and can't be read here).
  const finalTranscriptRef = useRef('')

  const wsRef = useRef<WebSocket | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const nodeRef = useRef<AudioWorkletNode | null>(null)

  const stop = useCallback(() => {
    nodeRef.current?.disconnect()
    nodeRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'Terminate' }))
    }
    wsRef.current?.close()
    wsRef.current = null
    void ctxRef.current?.close()
    ctxRef.current = null
    setPartialTranscript('')
    setIsRecording(false)
  }, [])

  // Release mic / WS / AudioContext if the screen unmounts mid-session.
  useMountEffect(() => () => stop())

  const start = useCallback(async () => {
    setError(undefined)
    setFinalTranscript('')
    finalTranscriptRef.current = ''
    setPartialTranscript('')
    try {
      const { token } = await api.getSpeechToken()
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const ctx = new AudioContext()
      ctxRef.current = ctx
      await ctx.resume() // iOS: must resume after a user gesture
      await ctx.audioWorklet.addModule(
        new URL('../audio/pcmWorklet.js', import.meta.url).href
      )

      const ws = new WebSocket(`${WS_BASE}?${WS_PARAMS}&token=${token}`)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onmessage = (event) => {
        // Non-JSON frames (keep-alives, binary control, protocol errors) would
        // otherwise throw synchronously here and surface as unhandled errors.
        let msg: Record<string, unknown> & {
          type?: string
          end_of_turn?: boolean
          transcript?: string
        }
        try {
          msg = JSON.parse(event.data)
        } catch {
          return
        }
        if (msg.type !== 'Turn') return
        if (msg.end_of_turn) {
          const next =
            `${finalTranscriptRef.current} ${msg.transcript ?? ''}`.trim()
          finalTranscriptRef.current = next
          setFinalTranscript(next)
          setPartialTranscript('')
          // Side effect fired from the WS event handler (not render), so it runs
          // exactly once per finalized turn — safe under Strict Mode.
          onTurnFinalizedRef.current?.(next)
        } else {
          setPartialTranscript(msg.transcript ?? '')
        }
      }
      ws.onerror = () =>
        setError('Speech connection error. You can still type details.')

      const source = ctx.createMediaStreamSource(stream)
      const node = new AudioWorkletNode(ctx, 'pcm-worklet')
      nodeRef.current = node
      node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(e.data)
        }
      }
      source.connect(node)
      // Intentionally NOT connected to ctx.destination (avoids echo/feedback).
      setIsRecording(true)
    } catch {
      setError(
        'Could not access the microphone. You can type the details instead.'
      )
      stop()
    }
  }, [api, stop])

  return { isRecording, finalTranscript, partialTranscript, error, start, stop }
}
